import {
  PublicZadCampaign,
  PublicZadCatalogueProduct,
  PublicZadLocalizedText,
  PublicZadProductPromotion,
} from '../../../models/zadPublicCatalogue'
import {formatZadTnd} from '../zad/productHelpers'

export const PUBLIC_ZAD_OFFERS_LIMIT = 8

export function emptyPublicLocalized(): PublicZadLocalizedText {
  return {fr: '', en: '', ar: ''}
}

export function sanitizePublicLocalized(value: unknown): PublicZadLocalizedText {
  if (typeof value === 'string') {
    return {fr: value.trim(), en: '', ar: ''}
  }
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  return {
    fr: String(raw.fr || '').trim(),
    en: String(raw.en || '').trim(),
    ar: String(raw.ar || '').trim(),
  }
}

export function pickPublicLocalized(value: PublicZadLocalizedText | undefined, locale: string): string {
  const text = value || emptyPublicLocalized()
  const lang = String(locale || 'fr').slice(0, 2)
  return text[lang as keyof PublicZadLocalizedText] || text.fr || text.en || text.ar || ''
}

function asDiscountType(value: unknown): 'PERCENTAGE' | 'FIXED_AMOUNT' | null {
  if (value === 'PERCENTAGE' || value === 'FIXED_AMOUNT') return value
  return null
}

export function sanitizePublicZadProductPromotion(value: unknown): PublicZadProductPromotion | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const discountType = asDiscountType(raw.discountType)
  const promotionalPrice =
    typeof raw.promotionalPrice === 'number' ? raw.promotionalPrice : Number(raw.promotionalPrice)
  const originalPrice =
    typeof raw.originalPrice === 'number' ? raw.originalPrice : Number(raw.originalPrice)
  const discountValue =
    typeof raw.discountValue === 'number' ? raw.discountValue : Number(raw.discountValue)
  if (!discountType || !Number.isFinite(promotionalPrice) || !Number.isFinite(discountValue)) {
    return null
  }
  return {
    title: sanitizePublicLocalized(raw.title),
    discountType,
    discountValue,
    originalPrice: Number.isFinite(originalPrice) ? originalPrice : 0,
    promotionalPrice,
    validUntil: raw.validUntil ? String(raw.validUntil) : null,
  }
}

export function sanitizePublicZadCampaign(value: unknown): PublicZadCampaign | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const title = sanitizePublicLocalized(raw.title)
  const discountType = asDiscountType(raw.discountType)
  const discountValue =
    typeof raw.discountValue === 'number' ? raw.discountValue : Number(raw.discountValue)
  if (!discountType || !Number.isFinite(discountValue)) return null
  if (!title.fr && !title.en && !title.ar) return null
  return {
    title,
    description: sanitizePublicLocalized(raw.description),
    discountType,
    discountValue,
    startAt: raw.startAt ? String(raw.startAt) : null,
    endAt: raw.endAt ? String(raw.endAt) : null,
    isFeatured: raw.isFeatured === true,
    bannerImageUrl: raw.bannerImageUrl ? String(raw.bannerImageUrl) : null,
  }
}

export function extractPublicPromotionsList(data: unknown): PublicZadCampaign[] {
  const raw = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  const source = Array.isArray(raw.data) ? raw.data : raw.promotions
  if (!Array.isArray(source)) return []
  return source
    .map(sanitizePublicZadCampaign)
    .filter((item): item is PublicZadCampaign => Boolean(item))
}

export function pickFeaturedCampaign(campaigns: PublicZadCampaign[]): PublicZadCampaign | null {
  return campaigns.find((item) => item.isFeatured) || null
}

export function formatPublicDiscountLabel(
  promotion: Pick<PublicZadProductPromotion, 'discountType' | 'discountValue'>
): string {
  if (promotion.discountType === 'PERCENTAGE') return `-${promotion.discountValue}%`
  return `-${formatZadTnd(promotion.discountValue)}`
}

export function getPublicZadDisplayPrice(product: PublicZadCatalogueProduct) {
  const promotion = product.promotion || null
  const hasPromotion = Boolean(promotion)
  const normalPrice = hasPromotion ? promotion!.originalPrice : product.salePrice
  const effectivePrice = hasPromotion ? promotion!.promotionalPrice : product.salePrice
  return {
    normalPrice,
    effectivePrice,
    hasPromotion,
    discountLabel: hasPromotion ? formatPublicDiscountLabel(promotion!) : '',
    title: promotion?.title || emptyPublicLocalized(),
    validUntil: promotion?.validUntil || null,
    salePrice: product.salePrice,
  }
}

export function formatPublicValidUntil(value?: string | null, locale = 'fr'): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {dateStyle: 'long'}).format(date)
}

export function collectPromotedProducts(
  groups: PublicZadCatalogueProduct[][],
  limit = PUBLIC_ZAD_OFFERS_LIMIT
): PublicZadCatalogueProduct[] {
  const seen = new Set<string>()
  const out: PublicZadCatalogueProduct[] = []
  groups.forEach((group) => {
    group.forEach((product) => {
      if (!product?.id || seen.has(product.id)) return
      if (!getPublicZadDisplayPrice(product).hasPromotion) return
      seen.add(product.id)
      out.push(product)
    })
  })
  return out.slice(0, limit)
}

export function campaignDiscountLabel(campaign: PublicZadCampaign): string {
  return formatPublicDiscountLabel(campaign)
}
